/* ============================================================
   Product page — Intercom launcher offset controller
   Loaded only from sections/main-product.liquid.

   Keeps the Intercom support button clear of the mobile bottom
   dock/navigation and the sticky Add to Cart bar by writing a
   single CSS custom property:

       --intercom-product-page-bottom-offset

   which the scoped CSS in product-page-custom.css consumes.

   Behaviour:
   - Nothing visible            -> button rests bottom-right (REST).
   - An obstacle becomes visible -> button is lifted so it sits
     GAP px above the TOP of the tallest visible obstacle.
   - Heights are measured live, never hardcoded.
   - Tracks the obstacles' show/hide animations frame-by-frame
     (rAF) so the move is smooth and never jumps aggressively.

   No global side effects: the property is only read by
   body.template-product selectors and this script only runs on
   the product template.
   ============================================================ */
(function () {
  if (window.__lafloreIntercomOffsetInit) return;
  if (!document.body || !document.body.classList.contains('template-product')) return;
  window.__lafloreIntercomOffsetInit = true;

  var REST = 20;       // px — natural bottom-right resting position
  var GAP = 30;        // px — clearance kept above the tallest visible obstacle
  var MAX_IDLE = 60;   // frames of no change before the rAF loop parks itself

  // Every form the launcher can take. Intercom rewrites these elements' inline
  // styles (often with !important) and swaps between them when the Messenger is
  // opened/closed, so we re-assert our offset directly on whichever exists.
  // NOTE: deliberately excludes the open messenger panel
  // (.intercom-messenger-frame) and the unread badge / discovery teaser, which
  // have their own positioning.
  var LAUNCHER_SELECTOR = [
    '.intercom-lightweight-app-launcher',
    '.intercom-lightweight-app',
    '.intercom-launcher-frame',
    '.intercom-launcher'
  ].join(',');

  var root = document.documentElement;
  var rafId = null;
  var idleFrames = 0;
  var lastBottom = null;

  function dock() {
    return document.querySelector('.mobile-dock');
  }

  function stickyCard() {
    var form = document.querySelector('.product-sticky-form');
    return form ? (form.querySelector('.product-sticky-form__card') || form.firstElementChild) : null;
  }

  // Distance (px) from the bottom of the viewport up to the top edge of an
  // element, but only while that element is actually rendered & visible.
  // Returns 0 for hidden / off-screen elements so they contribute nothing.
  function clearanceOf(el, easeByOpacity) {
    if (!el) return 0;

    var cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return 0;

    var opacity = parseFloat(cs.opacity);
    if (isNaN(opacity)) opacity = 1;
    if (opacity <= 0.01) return 0;

    var rect = el.getBoundingClientRect();
    if (rect.height <= 0) return 0;

    var raw = window.innerHeight - rect.top;
    if (raw <= 0) return 0;

    // Ease the contribution by opacity so a fading obstacle lifts/drops the
    // launcher smoothly rather than snapping the instant it toggles.
    return easeByOpacity ? raw * opacity : raw;
  }

  function targetBottom() {
    // Dock slides in via transform (rect.top eases) -> no opacity easing.
    // Sticky bar fades in via opacity -> ease by opacity.
    // Taking the max naturally covers the "both visible" stacked case,
    // because the sticky bar is itself translated above the dock.
    var clearance = Math.max(
      clearanceOf(dock(), false),
      clearanceOf(stickyCard(), true)
    );
    return clearance > 0.5 ? Math.round(clearance + GAP) : REST;
  }

  // `bottom` only works on a positioned element. In modern Intercom the
  // launcher iframe (.intercom-launcher-frame) is position:static and sits
  // inside a positioned wrapper div whose class is hashed per session (e.g.
  // .intercom-with-namespace-4wz414). So for any launcher element we walk up to
  // the nearest *positioned* Intercom ancestor — that's the element `bottom`
  // actually moves. (For the lightweight launcher, which is positioned itself,
  // this returns the element unchanged.)
  function positionedTarget(el) {
    var hops = 0;
    while (el && hops < 6) {
      if (isIntercomNode(el) && getComputedStyle(el).position !== 'static') {
        return el;
      }
      el = el.parentElement;
      hops++;
    }
    return null;
  }

  // Re-assert the offset straight onto the launcher's positioned element with
  // an inline !important rule. This wins even against Intercom's own inline
  // !important and survives its re-renders. Guarded by a diff check so it never
  // fights itself in an infinite mutation loop.
  function enforceInline(bottomPx) {
    var nodes = document.querySelectorAll(LAUNCHER_SELECTOR);
    var changed = false;
    var done = [];
    for (var i = 0; i < nodes.length; i++) {
      var target = positionedTarget(nodes[i]);
      if (!target || done.indexOf(target) !== -1) continue;
      done.push(target);
      if (target.style.getPropertyValue('bottom') !== bottomPx ||
          target.style.getPropertyPriority('bottom') !== 'important') {
        target.style.setProperty('bottom', bottomPx, 'important');
        changed = true;
      }
    }
    return changed;
  }

  function apply() {
    var bottom = targetBottom();
    var changed = false;

    if (bottom !== lastBottom) {
      lastBottom = bottom;
      root.style.setProperty('--intercom-product-page-bottom-offset', bottom + 'px');
      changed = true;
    }

    // Even when the computed offset is unchanged, Intercom may have reset the
    // launcher (e.g. right after closing the widget) — re-apply if so.
    if (enforceInline(bottom + 'px')) changed = true;

    return changed;
  }

  function frame() {
    var changed = apply();
    idleFrames = changed ? 0 : idleFrames + 1;
    rafId = idleFrames < MAX_IDLE ? requestAnimationFrame(frame) : null;
  }

  function wake() {
    idleFrames = 0;
    if (rafId === null && !document.hidden) {
      rafId = requestAnimationFrame(frame);
    }
  }

  // One-shot, rAF-coalesced re-apply. Used by the Intercom watchers and the
  // safety interval: it corrects the launcher once without keeping the smooth
  // animation loop spinning. Coalescing many triggers into a single frame is
  // what prevents an infinite loop with the MutationObserver (our own inline
  // write fires the observer, but enforceInline is diff-guarded so the next
  // apply is a no-op and nothing further mutates).
  var safetyScheduled = false;
  function scheduleApply() {
    if (safetyScheduled || document.hidden) return;
    safetyScheduled = true;
    requestAnimationFrame(function () {
      safetyScheduled = false;
      apply();
    });
  }

  // Re-assert across the frames following an Intercom interaction, because the
  // launcher is re-rendered asynchronously some time after the click/close.
  function reassertBurst() {
    scheduleApply();
    [60, 150, 300, 600, 1000, 1600].forEach(function (ms) {
      setTimeout(scheduleApply, ms);
    });
  }

  // --- Triggers -------------------------------------------------------------
  window.addEventListener('scroll', wake, { passive: true });
  window.addEventListener('resize', wake);
  window.addEventListener('orientationchange', wake);
  window.addEventListener('load', wake);
  document.addEventListener('matchSmall', wake);   // theme breakpoint change
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) wake();
  });

  // Catch obstacles that appear/disappear without a scroll event.
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(wake);
    var d = dock();
    if (d) {
      mo.observe(d, { attributes: true, attributeFilter: ['class', 'style'] });
      var section = d.closest('.mobile-dock-section');
      if (section) mo.observe(section, { attributes: true, attributeFilter: ['class'] });
    }
    var card = stickyCard();
    if (card) mo.observe(card, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(wake, { threshold: [0, 0.01, 1] });
    var dEl = dock();
    if (dEl) io.observe(dEl);
    var form = document.querySelector('.product-sticky-form');
    if (form) io.observe(form);
  }

  // --- Keep the offset after Intercom opens / closes / re-renders -----------
  // Intercom loads asynchronously, swaps the launcher between an iframe
  // (.intercom-launcher-frame) and the lightweight markup
  // (.intercom-lightweight-app[-launcher]), and rewrites their inline styles
  // (with !important) on every open/close. CSS alone can't win against inline
  // !important, so we watch Intercom's DOM and re-assert our calculated offset
  // directly. Every layer below funnels into scheduleApply(), which is
  // rAF-coalesced + diff-guarded, so none of them can loop on each other.

  function isIntercomNode(node) {
    if (!node || node.nodeType !== 1) return false;
    var id = node.id || '';
    var cls = (typeof node.className === 'string')
      ? node.className
      : ((node.getAttribute && node.getAttribute('class')) || '');
    return id.indexOf('intercom') !== -1 || cls.indexOf('intercom') !== -1;
  }

  // Focused observers, one per Intercom top-level node, watching style/class
  // rewrites and launcher swaps within that subtree. Scoping the subtree
  // observation to Intercom's own nodes keeps it cheap on a busy page.
  function attachToIntercomSubtrees() {
    if (!('MutationObserver' in window) || !document.body) return;
    var kids = document.body.children;
    for (var i = 0; i < kids.length; i++) {
      var node = kids[i];
      if (!isIntercomNode(node) || node.__lafloreLauncherObserved) continue;
      node.__lafloreLauncherObserved = true;
      new MutationObserver(scheduleApply).observe(node, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: true,
        subtree: true
      });
      scheduleApply();
    }
  }

  // Catch Intercom inserting / replacing its top-level nodes (boot, swap
  // between lightweight and full launcher, etc.).
  if ('MutationObserver' in window && document.body) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (isIntercomNode(added[j])) {
            attachToIntercomSubtrees();
            reassertBurst();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true });
  }

  // Clicking the lightweight launcher (a div in the host document) triggers a
  // re-render; clicks inside the iframe launcher don't reach us, so onShow/the
  // observers cover that case instead.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest(LAUNCHER_SELECTOR + ',[class*="intercom"]')) {
      reassertBurst();
    }
  }, true);

  // Native Intercom lifecycle events, if the messenger is present.
  var intercomHooked = false;
  function hookIntercomEvents() {
    if (intercomHooked || typeof window.Intercom !== 'function') return;
    try {
      window.Intercom('onShow', reassertBurst);
      window.Intercom('onHide', reassertBurst);
      intercomHooked = true;
    } catch (e) { /* ignore — the DOM observers are the fallback */ }
  }

  // Intercom may load after us — attach hooks/observers as soon as it appears.
  var intercomTries = 0;
  function pollForIntercom() {
    hookIntercomEvents();
    attachToIntercomSubtrees();
    scheduleApply();
    intercomTries++;
    if (intercomHooked && document.getElementById('intercom-container')) return;
    if (intercomTries > 60) return; // give up scanning after ~30s
    setTimeout(pollForIntercom, 500);
  }

  // Safety net: a slow, cheap, one-shot correction so the launcher can never
  // stay misplaced for more than ~1.2s even if every event/observer is missed.
  // scheduleApply is diff-guarded, so this writes nothing when already correct.
  setInterval(scheduleApply, 1200);

  // Initial paint.
  scheduleApply();
  pollForIntercom();
})();
